/**
 * ZeroSmoke brand logo — SVG icon mark + wordmark variants.
 *
 * Variants:
 *   <ZeroSmokeLogo />        — icon only  (navbar, favicon-size)
 *   <ZeroSmokeLogoFull />    — icon + wordmark  (footer, splash)
 */

interface LogoProps {
  size?: number;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Icon mark — a bold shield with stylised "Z" + leaf + zero ring    */
/* ------------------------------------------------------------------ */
export function ZeroSmokeLogo({ size = 40, className = '' }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="ZeroSmoke logo"
    >
      <defs>
        {/* Background gradient — deep charcoal → near-black */}
        <linearGradient id="zs-bg" x1="0" y1="0" x2="120" y2="120" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1a1a2e" />
          <stop offset="1" stopColor="#0a0a0a" />
        </linearGradient>

        {/* Emerald accent gradient */}
        <linearGradient id="zs-em" x1="25" y1="20" x2="95" y2="100" gradientUnits="userSpaceOnUse">
          <stop stopColor="#34d399" />
          <stop offset="1" stopColor="#059669" />
        </linearGradient>

        {/* Glow filter */}
        <filter id="zs-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Rounded-square background */}
      <rect width="120" height="120" rx="28" fill="url(#zs-bg)" />

      {/* Subtle outer ring */}
      <rect
        x="3" y="3" width="114" height="114" rx="26"
        fill="none" stroke="url(#zs-em)" strokeWidth="1.5" opacity="0.35"
      />

      {/* ── "Zero" ring — open circle at top-right ── */}
      <circle
        cx="60" cy="58" r="32"
        fill="none" stroke="url(#zs-em)" strokeWidth="4"
        strokeDasharray="175 26" strokeDashoffset="-12"
        strokeLinecap="round" opacity="0.25"
      />

      {/* ── Bold Z letterform ── */}
      <g filter="url(#zs-glow)">
        <path
          d="
            M 38 38
            L 82 38
            L 82 44
            L 51 78
            L 82 78
            L 82 84
            L 38 84
            L 38 78
            L 69 44
            L 38 44
            Z
          "
          fill="url(#zs-em)"
        />
      </g>

      {/* ── Leaf accent — curves out of the Z's bottom-right ── */}
      <path
        d="M 84 76 C 90 68, 100 64, 100 52 C 94 58, 88 62, 84 72"
        fill="#10b981" opacity="0.7"
      />
      <path
        d="M 86 74 C 90 66, 96 62, 96 54"
        fill="none" stroke="#34d399" strokeWidth="1" strokeLinecap="round" opacity="0.5"
      />

      {/* ── Tiny sparkle dots — subtle premium feel ── */}
      <circle cx="96" cy="48" r="2" fill="#34d399" opacity="0.6" />
      <circle cx="26" cy="34" r="1.5" fill="#34d399" opacity="0.3" />
      <circle cx="30" cy="92" r="1.5" fill="#10b981" opacity="0.25" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Full logo — icon + "ZeroSmoke" wordmark                           */
/* ------------------------------------------------------------------ */
interface FullLogoProps {
  iconSize?: number;
  className?: string;
  dark?: boolean;
}

export function ZeroSmokeLogoFull({ iconSize = 36, className = '', dark = true }: FullLogoProps) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <ZeroSmokeLogo size={iconSize} />
      <span className="flex flex-col leading-none select-none">
        <span
          className="font-extrabold tracking-tight"
          style={{ fontSize: iconSize * 0.52, lineHeight: 1.1 }}
        >
          <span className="text-emerald-500">Zero</span>
          <span className={dark ? 'text-white' : 'text-gray-900'}>Smoke</span>
        </span>
        <span
          className="uppercase tracking-[0.25em] text-emerald-500/60 font-semibold"
          style={{ fontSize: iconSize * 0.2, lineHeight: 1.4 }}
        >
          nicotine pouches
        </span>
      </span>
    </span>
  );
}

export default ZeroSmokeLogo;
